import { createLumeFxRestClient } from '@/services/LumeFxService';
import { addMarketFull } from '@/services/MarketService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentLuceraAccountName } from '@/state/reducers/authSlice';
import { OrderPrefillValues } from '@/state/reducers/orderSlice';
import { selectSettings } from '@/state/reducers/settingSlice';
import { LumeFxOrderType, LumeFxSide, LumeFxTimeInForce } from '@/types/lumeFx';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import { UseOrderExecutionReturn } from '@/utils/hooks/useOrderExecution';
import useTotals from '@/utils/hooks/useTotals';
import useTradeFeePreview from '@/utils/hooks/useTradeFeePreview';
import { useLucera } from '@/utils/providers/LuceraProvider';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import FeePreview from '../common/FeePreview';
import ExecutionInputController from '../inputs/ExecutionInputController';
import RHFNumberInput from '../inputs/RHFNumberInput';
import Select from '../inputs/Select';
import { BalancePreview } from './components/BalancePreview';
import FormBody from './components/FormBody';
import FormFooter from './components/FormFooter';
import FormOrderTypeSwitch from './components/FormOrderTypeSwitch';
import FormSubmit from './components/FormSubmit';
import Totals from './components/Totals';
import { MarketOrderFormInput, MarketOrderFormValues, marketOrderSchema } from './schema/marketOrderSchema';

const durationOptions: { label: keyof typeof LumeFxTimeInForce; value: LumeFxTimeInForce }[] = [
    { label: 'IOC', value: LumeFxTimeInForce.IOC },
    { label: 'FOK', value: LumeFxTimeInForce.FOK }
];

const defaultValues: Partial<MarketOrderFormInput> = {
    quantity: null,
    duration: durationOptions[0]
};

interface MarketOrderFormProps extends UseOrderExecutionReturn {
    parentSize?: number;
    prefillValues?: OrderPrefillValues;
    onClose?: () => void;
    closeOnSubmit?: boolean;
}

const luceraRestURL = window.config?.lucera?.rest;

function MarketOrderForm(props: MarketOrderFormProps) {
    const { parentSize = 0, prefillValues, onClose, closeOnSubmit = false, ...orderExecutionProps } = props;

    const {
        mode,
        ccy1,
        ccy2,
        sideState,
        currencyState,
        orderBook,
        instrument,
        activeCurrencyConfig,
        pairMapToUse,
        ccy1Config,
        isCcy2Order,
        minOrderSize,
        orderDecimals
    } = orderExecutionProps;

    const lang = useLanguage();
    const settings = useAppSelector(selectSettings);
    const credentials = useAppSelector(selectCredentials);
    const currentLuceraAccountName = useAppSelector(selectCurrentLuceraAccountName);

    const { jwt } = useLucera();

    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });

    const [side, setSide] = sideState;
    const [currency, setCurrency] = currencyState;

    const { asks, bids, bestPrice } = orderBook;
    const { increments } = activeCurrencyConfig;

    const form = useForm<MarketOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            marketOrderSchema({
                min_order_size: minOrderSize,
                order_decimals: orderDecimals,
                currencyOut: currency,
                currencyPair: pairMapToUse?.show
            })
        )
    });

    const totals = useTotals({
        form,
        pairMap: pairMapToUse,
        side,
        ccy1: instrument?.type === 'Future' ? instrument?.config?.ccy2 || '' : ccy1,
        ccy2: instrument?.type === 'Future' ? instrument?.config?.ccy2 || '' : ccy2,
        ccy2Order: isCcy2Order,
        bids,
        asks
    });

    const {
        watch,
        reset,
        register,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const [quantity] = watch(['quantity']);
    const [debouncedQuantity] = useDebouncedValue(quantity, 300);

    const isCfd = useMemo(() => (instrument.config?.subType === 'Spot' ? false : true), [instrument]);

    const { spotFeesApi, cfdFeesApi } = useTradeFeePreview({
        ...orderExecutionProps,
        side,
        isCfd,
        debouncedQuantity
    });

    const { data: spotFees } = spotFeesApi;
    const { data: cfdFees } = cfdFeesApi;

    const fees = useMemo(() => spotFees || cfdFees, [spotFees, cfdFees]);

    const onSubmit = async (data: MarketOrderFormInput, e) => {
        const dataValidated = data as MarketOrderFormValues;
        if (dataValidated.quantity && credentials) {
            if (closeOnSubmit) onClose?.();
            lumeFxRestClient.placeOrder({
                Account: currentLuceraAccountName,
                Symbol: pairMapToUse?.celer,
                TimeInForce: dataValidated.duration.value as LumeFxTimeInForce,
                OrdType: LumeFxOrderType.Market,
                OrderQty: (data.quantity || 0).toString(),
                ClOrdID: crypto.randomUUID(),
                Side: side
            });
        }
    };

    const onError = (errors) => console.error(errors);

    useDidUpdate(() => reset(defaultValues), [currency]);

    useDidUpdate(() => {
        if (credentials) {
            if (debouncedQuantity && debouncedQuantity > 0) {
                // console.log('addMarketFull due to quantity', market.celer);
                addMarketFull(pairMapToUse.celer);
            }
        }
    }, [debouncedQuantity]);

    useEffect(() => {
        if (prefillValues) {
            setSide((prefillValues.netQtyPosition || 0) < 0 ? LumeFxSide.SELL : LumeFxSide.BUY);
            reset({
                ...defaultValues,
                duration: settings.trading.defaultMarketOrderDuration,
                quantity: +Math.abs(prefillValues.netQtyPosition).toFixed(ccy1Config.order_decimals)
            });
        } else reset({ ...defaultValues, duration: settings.trading.defaultMarketOrderDuration });
    }, [pairMapToUse.celer, prefillValues]);

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="relative flex flex-col h-full w-full text-brand-text">
                <FormBody side={side} mode={mode === 'popup' ? 'Modal' : 'Module'}>
                    <FormOrderTypeSwitch initialValue={side} onChange={setSide} />
                    <ExecutionInputController name="duration" label={`${lang.orderForm.duration} *`}>
                        <Select type="execution" options={durationOptions} />
                    </ExecutionInputController>
                    <RHFNumberInput
                        {...register('quantity')}
                        label={`${lang.orderForm.quantity} *`}
                        placeholder={lang.orderForm.quantity}
                        min={minOrderSize}
                        step={increments}
                        currency={activeCurrencyConfig.show}
                    />
                    <Totals {...totals} />
                    {!isCfd && debouncedQuantity && (
                        <FeePreview
                            feesDetails={spotFees}
                            isLoadingFeesDetails={spotFeesApi.isLoading || spotFeesApi.isValidating}
                            feesDetailsError={spotFeesApi.error}
                        />
                    )}
                    {isCfd && debouncedQuantity && (
                        <FeePreview
                            feesDetails={cfdFees}
                            isLoadingFeesDetails={cfdFeesApi.isLoading || cfdFeesApi.isValidating}
                            feesDetailsError={cfdFeesApi.error}
                        />
                    )}
                    {totals.total !== '-' && debouncedQuantity && (
                        <BalancePreview
                            fees={fees}
                            totals={totals}
                            quantity={debouncedQuantity}
                            parentSize={parentSize}
                            {...orderExecutionProps}
                        />
                    )}
                </FormBody>
                <FormFooter mode={mode === 'popup' ? 'Modal' : 'Module'}>
                    <FormSubmit
                        side={side}
                        isValid={isValid}
                        disabled={isSubmitting || !isValid || totals.disableSubmit || !instrument.type}
                        noInstrumentConfig={!instrument.type}
                        noSettlementDate={!orderBook.settlementDate}
                        className={mode === 'popup' ? 'left-6' : ''}
                    />
                </FormFooter>
            </form>
        </FormProvider>
    );
}

export default MarketOrderForm;
